import { createFileRoute, useNavigate } from '@tanstack/react-router';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Form from '@nineeng/forms';
import React, { useCallback, useState } from 'react';
import { Tabs, CopyToClipboardChip } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useConfig } from 'src/hooks/useConfig';
import CircularProgress from '@mui/material/CircularProgress';
import { getSettings, updateSettings, testCheckinatwork } from 'src/api/admin/settings';
import type { TestCheckinatworkResult } from 'src/api/admin/settings';
import { getAllZones } from 'src/api/admin/zones';
import { getAllEventTypes } from 'src/api/admin/types';
import { Role, Type } from '@repo/shared/domain';
import { getRolesForAutocomplete } from 'src/api/admin/roles';

interface Zone {
	id: string;
	name: string;
}

interface CalendarSettings {
	enabled?: boolean;
	domainAllowList?: string[];
	domainIgnoreList?: string[];
	defaultRole?: string;
	allowMinutesPre?: number;
	allowMinutesPost?: number;
	eventType?: string;
}

interface CheckinatworkSettings {
	enabled?: boolean;
	publicKey?: string | null;
	privateKey?: string | null;
	certificate?: string | null;
	zoneMapping?: Record<string, string>;
	expeditorNumber?: string | null;
	companyId?: string | null;
	useTestEnv?: boolean;
}

interface TenantSyncSettings {
	enabled?: boolean;
	debug?: boolean;
	baseUrl?: string;
	syncRoleIds?: string[];
}

interface PlayitSettings {
	email?: string;
	password?: string;
	organizationId?: string;
	apiUrl?: string;
	redirectUrl?: string;
	clientId?: string;
	clientSecret?: string;
}

const defaultSettings: CalendarSettings = {
	enabled: false,
	allowMinutesPre: 15,
	allowMinutesPost: 15,
};

const defaultCheckinatworkSettings: CheckinatworkSettings = {
	enabled: false,
	publicKey: null,
	privateKey: null,
	certificate: null,
	zoneMapping: {},
};

const defaultTenantSyncSettings: TenantSyncSettings = {
	enabled: false,
	debug: false,
	baseUrl: '',
	syncRoleIds: [],
};

export const Route = createFileRoute('/_admin/system/integrations')({
	validateSearch: (search: Record<string, unknown>) => ({
		tab: (search.tab as string) || 'calendar',
	}),
	loader: async () => {
		const [
			calendarSettings,
			checkinatworkSettings,
			tenantSyncSettings,
			playitSettings,
			roles,
			eventTypesResponse,
			zonesResponse,
		] = await Promise.all([
			getSettings<{ settings: CalendarSettings }>('calendar'),
			getSettings<{ settings: CheckinatworkSettings }>('checkinatwork'),
			getSettings<{ settings: TenantSyncSettings }>('tenant-sync'),
			getSettings<{ settings: PlayitSettings }>('playit'),
			getRolesForAutocomplete(),
			getAllEventTypes('noPagination=true'),
			getAllZones(),
		]);

		return {
			calendarSettings: calendarSettings?.settings || {},
			checkinatworkSettings: checkinatworkSettings?.settings || {},
			tenantSyncSettings: tenantSyncSettings?.settings || {},
			playitSettings: playitSettings?.settings || {},
			roles,
			eventTypes: eventTypesResponse?.data || [],
			zones: zonesResponse?.data || [],
		};
	},
	component: IntegrationsPage,
});

// Tab Components
const CalendarTab = ({
	calendarData,
	onChange,
	originalCalendarData,
	inviteEmail,
	roles,
	eventTypes,
}: {
	calendarData: CalendarSettings;
	onChange: (data: any) => void;
	originalCalendarData: CalendarSettings;
	inviteEmail: string;
	roles: Role[];
	eventTypes: Type[];
}) => {
	const disabled = !calendarData.enabled;

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						name: 'enabled',
						rowLabel: 'Enabled',
						type: 'Checkbox',
					},
					{
						name: 'email',
						rowLabel: 'Invite email address',
						rowDescription: 'Needs to be included as a participant in calendar invites',
						type: 'Custom',
						Component: () => (
							<CopyToClipboardChip disabled={!calendarData.enabled} value={calendarData.enabled ? inviteEmail : '-'} />
						),
					},
					{
						name: 'domainAllowList',
						rowLabel: 'Domain Allowlist',
						rowDescription: 'List of domains that are allowed to create invites through email',
						type: 'AutoComplete',
						disabled,
						multiple: true,
						options: [],
						freeSolo: true,
						handleHomeEndKeys: true,
						getOptionLabel: (option: any) => {
							if (typeof option === 'string') {
								return option;
							}
							return option?.id;
						},
						renderOption: (props: any, option: any) => {
							return <li {...props}>{option?.name}</li>;
						},
						filterOptions: (options: any[], params: any) => {
							const filtered = [...options];
							const { inputValue } = params;
							if (inputValue) {
								filtered.push({
									id: inputValue,
									inputValue,
									name: `Add "${inputValue}"`,
								});
							}
							return filtered;
						},
					},
					{
						name: 'domainIgnoreList',
						rowLabel: 'Domain Ignorelist',
						rowDescription: 'List of domains that are ignored when creating invites through email',
						type: 'AutoComplete',
						disabled,
						multiple: true,
						options: [],
						freeSolo: true,
						handleHomeEndKeys: true,
						getOptionLabel: (option: any) => {
							if (typeof option === 'string') {
								return option;
							}
							return option?.id;
						},
						renderOption: (props: any, option: any) => {
							return <li {...props}>{option?.name}</li>;
						},
						filterOptions: (options: any[], params: any) => {
							const filtered = [...options];
							const { inputValue } = params;
							if (inputValue) {
								filtered.push({
									id: inputValue,
									inputValue,
									name: `Add "${inputValue}"`,
								});
							}
							return filtered;
						},
					},
					{
						name: 'defaultRole',
						rowLabel: 'Role',
						rowDescription: 'Role to assign to users when included in a calendar event',
						type: 'AutoCompleteByID',
						options: roles.map((r) => ({ ...r, id: r.id })),
						getOptionLabel: (o: Role) => o.name,
						disabled,
					},
					{
						name: 'allowMinutesPre',
						rowLabel: 'Grace period before',
						rowDescription: 'Grace period before event in minutes',
						type: 'TextField',
						htmlType: 'number',
						disabled,
					},
					{
						name: 'allowMinutesPost',
						rowLabel: 'Grace period after',
						rowDescription: 'Grace period after event in minutes',
						type: 'TextField',
						htmlType: 'number',
						disabled,
					},
					{
						name: 'eventType',
						rowLabel: 'Event Type',
						rowDescription: 'Events created from calendar events will use this type',
						type: 'AutoCompleteByID',
						options: eventTypes.map((z) => ({ ...z, id: z.id })),
						getOptionLabel: (o: Type) => o.name,
						disabled,
					},
				],
			},
		],
	};

	return (
		<>
			<Alert severity="info" sx={{ mb: 4 }}>
				When enabled and configured, calendar events sent to the configured email address will automatically trigger
				flows to ensure all participants are ready to checkin on site.
			</Alert>
			<Form
				key="calendar-form"
				schema={form}
				onChange={onChange}
				originalValues={originalCalendarData}
				CustomNavigation={() => null}
			/>
		</>
	);
};

const ZoneMappingComponent = ({
	values,
	onChange,
	zones,
}: {
	values: CheckinatworkSettings;
	onChange: (e: { target: { name: string; value: Record<string, string> } }) => void;
	zones: Zone[];
}) => {
	const name = 'zoneMapping';
	const value = values.zoneMapping || {};

	const mappedZoneIds = Object.keys(value);
	const availableZones = zones.filter((zone) => !mappedZoneIds.includes(zone.id));

	return (
		<div className="space-y-2">
			{Object.entries(value).map(([zoneId, uniqueId], index) => (
				<div key={index} className="flex items-center gap-2">
					<select
						value={zoneId}
						onChange={(e) => {
							const newMapping = { ...value };
							delete newMapping[zoneId];
							newMapping[e.target.value] = uniqueId;
							onChange({ target: { name, value: newMapping } });
						}}
						className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
					>
						<option value="">Select Zone</option>
						{zones.map((zone) => (
							<option key={zone.id} value={zone.id}>
								{zone.name}
							</option>
						))}
					</select>
					<input
						type="text"
						value={String(uniqueId || '')}
						onChange={(e) => {
							const newMapping = { ...value };
							newMapping[zoneId] = e.target.value;
							onChange({ target: { name, value: newMapping } });
						}}
						className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
						placeholder="Unique ID (e.g., 1Y102UT3YPQBZ)"
					/>
					<button
						type="button"
						onClick={() => {
							const newMapping = { ...value };
							delete newMapping[zoneId];
							onChange({ target: { name, value: newMapping } });
						}}
						className="px-3 py-2 text-red-600 hover:text-red-800"
					>
						Remove
					</button>
				</div>
			))}
			{availableZones.length > 0 && (
				<button
					type="button"
					onClick={() => {
						const newMapping = { ...value };
						newMapping[availableZones[0]?.id || ''] = '';
						onChange({ target: { name, value: newMapping } });
					}}
					className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
				>
					Add Zone Mapping
				</button>
			)}
		</div>
	);
};

const TestConfigComponent = ({
	checkinatworkData,
	zones,
}: {
	checkinatworkData: CheckinatworkSettings;
	zones: Zone[];
}) => {
	const [isTesting, setIsTesting] = useState(false);
	const [testResult, setTestResult] = useState<TestCheckinatworkResult | null>(null);
	const [testError, setTestError] = useState<string | null>(null);

	const getZoneName = (zoneId: string) => {
		const zone = zones.find((z) => z.id === zoneId);
		return zone?.name || zoneId;
	};

	const handleTestConfig = async () => {
		setIsTesting(true);
		setTestResult(null);
		setTestError(null);

		try {
			const result = await testCheckinatwork(checkinatworkData as Record<string, unknown>);
			setTestResult(result);
		} catch (err: any) {
			setTestError(err?.message || 'An unexpected error occurred while testing the configuration.');
		} finally {
			setIsTesting(false);
		}
	};

	return (
		<div className="w-full space-y-2">
			<Button
				variant="outlined"
				fullWidth
				onClick={handleTestConfig}
				disabled={isTesting}
				startIcon={isTesting ? <CircularProgress size={18} /> : undefined}
			>
				{isTesting ? 'Testing...' : 'Test Current Config'}
			</Button>

			{testError && (
				<Alert severity="error">
					{testError}
				</Alert>
			)}

			{testResult && !testResult.success && !testResult.tokenValid && (
				<Alert severity="error">
					<strong>Authentication failed</strong>
					<br />
					{testResult.error}
				</Alert>
			)}

			{testResult && testResult.tokenValid && (() => {
				const hasErrors = testResult.zoneResults.some((zr) => !zr.success);
				const hasWarnings = testResult.zoneResults.some((zr) => zr.success && zr.warning);
				const allClean = !hasErrors && !hasWarnings;

				if (allClean && testResult.zoneResults.length === 0) {
					return (
						<Alert severity="success">
							Authentication successful. No zone mappings configured to verify.
						</Alert>
					);
				}

				if (allClean) {
					return (
						<Alert severity="success">
							Configuration is valid. Authentication successful and all {testResult.zoneResults.length} zone(s) verified with registrations found.
						</Alert>
					);
				}

				return (
					<Alert severity={hasErrors ? 'error' : 'warning'}>
						<strong>
							{hasErrors
								? 'Authentication successful, but some zones failed:'
								: 'Authentication successful, but some zones need attention:'}
						</strong>
						<ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
							{testResult.zoneResults.map((zr) => (
								<li key={zr.zoneId} style={{ marginBottom: '4px' }}>
									<strong>{getZoneName(zr.zoneId)}</strong> ({zr.workplaceId}):{' '}
									{!zr.success ? (
										<span style={{ color: 'red' }}>{zr.error}</span>
									) : zr.warning ? (
										<span style={{ color: 'orange' }}>{zr.warning}</span>
									) : (
										<span style={{ color: 'green' }}>OK ({zr.resultCount} registration(s) found)</span>
									)}
								</li>
							))}
						</ul>
					</Alert>
				);
			})()}
		</div>
	);
};

const CheckinatworkTab = ({
	onChange,
	originalCheckinatworkData,
	checkinatworkData,
	zones,
}: {
	onChange: (data: any) => void;
	originalCheckinatworkData: CheckinatworkSettings;
	checkinatworkData: CheckinatworkSettings;
	zones: Zone[];
}) => {
	const ZoneMappingWithZones = React.useMemo(
		() => (props: any) => <ZoneMappingComponent {...props} zones={zones} />,
		[zones],
	);

	const TestConfigWithData = React.useMemo(
		() => () => <TestConfigComponent checkinatworkData={checkinatworkData} zones={zones} />,
		[checkinatworkData, zones],
	);

	const checkinatworkForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ name: 'enabled', rowLabel: 'Enabled', type: 'Checkbox' },
					{ name: 'certificate', rowLabel: 'Certificate', type: 'TextArea', rows: 6 },
					{ name: 'publicKey', rowLabel: 'Public Key', type: 'TextArea', rows: 6 },
					{ name: 'privateKey', rowLabel: 'Private Key', type: 'TextArea', rows: 6, hideValue: true },
				{ name: 'expeditorNumber', rowLabel: 'Expeditor Number', rowDescription: 'The expeditor number registered with the Social Security service for this certificate', type: 'TextField' },
				{ name: 'companyId', rowLabel: 'Company ID (KBO/BCE)', rowDescription: 'The KBO/BCE enterprise number or VAT number (e.g. 0123456789 or BE0123456789). Used to discover and validate workplace IDs.', type: 'TextField' },
				{ name: 'useTestEnv', rowLabel: 'Use Test Environment', type: 'Checkbox' },
					{
						name: 'zoneMapping',
						rowLabel: 'Zone to Unique ID Mapping',
						rowDescription: 'Map zone names to unique identifiers for the checkinatwork integration',
						type: 'Custom',
						Component: ZoneMappingWithZones,
					},
					{
						name: '_testConfig',
						rowLabel: 'Test Configuration',
						rowDescription: 'Validates the certificate, credentials, and each zone mapping against the Check-in at Work API',
						type: 'Custom',
						Component: TestConfigWithData,
					},
				],
			},
		],
	};

	return (
		<Form
			key="checkinatwork-form"
			schema={checkinatworkForm}
			onChange={onChange}
			originalValues={originalCheckinatworkData}
			CustomNavigation={() => null}
		/>
	);
};

const PlayitTab = ({
	onChange,
	originalPlayitData,
}: {
	onChange: (data: any) => void;
	originalPlayitData: PlayitSettings;
}) => {
	const playitForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ name: 'email', rowLabel: 'Username (email)', type: 'TextField' },
					{ name: 'password', rowLabel: 'password', type: 'TextField', htmlType: 'password', hideValue: true },
					{ name: 'organizationId', rowLabel: 'organizationId', type: 'TextField' },
					{ name: 'apiUrl', rowLabel: 'apiUrl', type: 'TextField' },
					{ name: 'redirectUrl', rowLabel: 'redirectUrl', type: 'TextField' },
					{ name: 'clientId', rowLabel: 'clientId', type: 'TextField' },
					{ name: 'clientSecret', rowLabel: 'clientSecret', type: 'TextField', htmlType: 'password' },
				],
			},
		],
	};

	return (
		<Form
			key="playit-form"
			schema={playitForm}
			onChange={onChange}
			originalValues={originalPlayitData}
			CustomNavigation={() => null}
		/>
	);
};

const SyncRoleIdsComponent = ({
	values,
	onChange,
}: {
	values: TenantSyncSettings;
	onChange: (e: { target: { name: string; value: string[] } }) => void;
}) => {
	const name = 'syncRoleIds';
	const value = values.syncRoleIds || [];

	return (
		<div className="space-y-2">
			{value.map((roleId, index) => (
				<div key={index} className="flex items-center gap-2">
					<input
						type="text"
						value={roleId}
						onChange={(e) => {
							const newRoleIds = [...value];
							newRoleIds[index] = e.target.value;
							onChange({ target: { name, value: newRoleIds } });
						}}
						placeholder="Enter role ID from source tenant"
						className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
						disabled={!values.enabled}
					/>
					<button
						type="button"
						onClick={() => {
							const newRoleIds = value.filter((_, i) => i !== index);
							onChange({ target: { name, value: newRoleIds } });
						}}
						className="px-3 py-2 text-red-600 hover:text-red-800"
						disabled={!values.enabled}
					>
						Remove
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={() => {
					const newRoleIds = [...value, ''];
					onChange({ target: { name, value: newRoleIds } });
				}}
				className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
				disabled={!values.enabled}
			>
				Add Role ID
			</button>
		</div>
	);
};

const TenantSyncTab = ({
	tenantSyncData,
	onChange,
	originalTenantSyncData,
}: {
	tenantSyncData: TenantSyncSettings;
	onChange: (data: any) => void;
	originalTenantSyncData: TenantSyncSettings;
}) => {
	const tenantSyncForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ name: 'enabled', rowLabel: 'Enabled', type: 'Checkbox' },
					{
						name: 'debug',
						rowLabel: 'Debug Mode',
						rowDescription: 'Enable additional logging for troubleshooting tenant sync',
						type: 'Checkbox',
						disabled: !tenantSyncData.enabled,
					},
					{
						name: 'baseUrl',
						rowLabel: 'Source Tenant Base URL',
						rowDescription: 'The base URL of the source tenant (e.g., https://source.nineid.cloud)',
						type: 'TextField',
						disabled: !tenantSyncData.enabled,
					},
					{
						name: 'syncRoleIds',
						rowLabel: 'Sync Role IDs',
						rowDescription: 'Roles to sync from the source tenant',
						type: 'Custom',
						Component: SyncRoleIdsComponent,
					},
				],
			},
		],
	};

	return (
		<>
			<Alert severity="info" sx={{ mb: 4 }}>
				Configure tenant data synchronization to automatically sync users, events, and access logs between tenants.
			</Alert>
			<Form schema={tenantSyncForm} onChange={onChange} originalValues={originalTenantSyncData} />
		</>
	);
};

function IntegrationsPage() {
	const loaderData = Route.useLoaderData();
	const navigate = useNavigate();
	const search = Route.useSearch();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const inviteEmail = `${config?.tenantId || ''}@invites.nineid.com`.toLowerCase();

	const [hasChanged, setHasChanged] = useState(false);
	const [calendarData, setCalendarData] = useState<CalendarSettings>({
		...defaultSettings,
		...loaderData.calendarSettings,
	});
	const [playitData, setPlayitData] = useState<PlayitSettings>({ ...loaderData.playitSettings });
	const [checkinatworkData, setCheckinatworkData] = useState<CheckinatworkSettings>({
		...defaultCheckinatworkSettings,
		...loaderData.checkinatworkSettings,
	});
	const [tenantSyncData, setTenantSyncData] = useState<TenantSyncSettings>({
		...defaultTenantSyncSettings,
		...loaderData.tenantSyncSettings,
	});

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Integrations');

	const originalCalendarData = { ...defaultSettings, ...loaderData.calendarSettings };
	const originalCheckinatworkData = { ...defaultCheckinatworkSettings, ...loaderData.checkinatworkSettings };
	const originalTenantSyncData = { ...defaultTenantSyncSettings, ...loaderData.tenantSyncSettings };
	const originalPlayitData = { ...loaderData.playitSettings };

	const calendarOnChange = useCallback((data: any) => {
		data.domainAllowList = (data.domainAllowList || []).map((domain: any) => {
			if (typeof domain === 'string') {
				if (domain.substring(0, 1) === '@') return domain.substring(1).trim().toLocaleLowerCase();
				return domain.trim().toLowerCase();
			}
			return domain.id?.trim().toLowerCase();
		});
		data.domainIgnoreList = (data.domainIgnoreList || []).map((domain: any) => {
			if (typeof domain === 'string') {
				if (domain.substring(0, 1) === '@') return domain.substring(1).trim().toLocaleLowerCase();
				return domain.trim().toLowerCase();
			}
			return domain.id?.trim().toLowerCase();
		});
		setCalendarData(data);
		setHasChanged(true);
	}, []);

	const playitOnChange = useCallback((data: any) => {
		setPlayitData(data);
		setHasChanged(true);
	}, []);

	const checkinatworkOnChange = useCallback((data: any) => {
		setCheckinatworkData(data);
		setHasChanged(true);
	}, []);

	const tenantSyncOnChange = useCallback((data: any) => {
		setTenantSyncData(data);
		setHasChanged(true);
	}, []);

	const saveChanges = async () => {
		const currentTab = tabs[activeTab];
		if (!currentTab) return;

		if (currentTab.label === 'Calendar Events') {
			await updateSettings('calendar', calendarData);
		} else if (currentTab.label === 'Check-in at Work') {
			await updateSettings('checkinatwork', checkinatworkData);
		} else if (currentTab.label === 'Tenant Sync') {
			await updateSettings('tenant-sync', tenantSyncData);
		} else if (currentTab.label === 'Playit') {
			await updateSettings('playit', playitData);
		}
		setHasChanged(false);
		window.location.reload();
	};

	const isCheckinatworkEnabled = featureFlags?.USE_CHECKINATWORK === true;
	const isTenantSyncEnabled = featureFlags?.USE_TENANT_SYNC === true;
	const isPlayitEnabled = featureFlags?.USES_PLAYIT === true;

	const tabs = [
		{
			key: 'calendar',
			label: 'Calendar Events',
			el: (
				<CalendarTab
					calendarData={calendarData}
					onChange={calendarOnChange}
					originalCalendarData={originalCalendarData}
					inviteEmail={inviteEmail}
					roles={loaderData.roles}
					eventTypes={loaderData.eventTypes}
				/>
			),
		},
		...(isCheckinatworkEnabled
			? [
					{
						key: 'checkinatwork',
						label: 'Check-in at Work',
						el: (
							<CheckinatworkTab
								onChange={checkinatworkOnChange}
								originalCheckinatworkData={originalCheckinatworkData}
								checkinatworkData={checkinatworkData}
								zones={loaderData.zones}
							/>
						),
					},
				]
			: []),
		...(isTenantSyncEnabled
			? [
					{
						key: 'tenant-sync',
						label: 'Tenant Sync',
						el: (
							<TenantSyncTab
								tenantSyncData={tenantSyncData}
								onChange={tenantSyncOnChange}
								originalTenantSyncData={originalTenantSyncData}
							/>
						),
					},
				]
			: []),
		...(isPlayitEnabled
			? [
					{
						key: 'playit',
						label: 'Playit',
						el: <PlayitTab onChange={playitOnChange} originalPlayitData={originalPlayitData} />,
					},
				]
			: []),
	];

	const activeTab = Math.max(
		0,
		tabs.findIndex((t) => t.key === search.tab),
	);

	return (
		<div className="flex flex-col p-8">
			<div className="flex mb-3">
				<Typography variant="h4">Integrations</Typography>
				<div className="flex-grow" />
				<Button disabled={!hasChanged} variant="outlined" onClick={saveChanges}>
					Save Changes
				</Button>
			</div>
			<Paper variant="outlined">
				<Tabs
					value={activeTab}
					onChange={(value) => {
						const nextTab = tabs[value];
						if (!nextTab) return;
						navigate({ to: '/system/integrations', search: { ...search, tab: nextTab.key } });
					}}
				>
					<Tabs.TabList>
						{tabs.map((t, index) => (
							<Tabs.Tab key={t.label} value={index} label={t.label} />
						))}
					</Tabs.TabList>
					{tabs.map((t, index) => (
						<Tabs.TabPanel key={t.label} value={index} selectedValue={activeTab}>
							<div>{t.el}</div>
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
